import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import { COMPACT_DATE_FIELD_WIDTH } from '~/components/common/CompactCivilDateField'
import { DateStepper } from '~/components/common/filters'
import { useJstMidnightTick } from '~/hooks/useJstMidnightTick'
import { startOfTodayJst } from '~/models/dateTime'

type Props = {
  /**
   * "表示日" / "今日" ラベルの i18n key prefix。
   * `${labelKeyPrefix}.displayDate` / `.today` を参照する。
   */
  labelKeyPrefix: string
  displayDate: Date
  /**
   * 日付が変わった時に呼ばれる (日付入力の直接編集 / 前日 / 翌日 の 3 経路)。
   * 前日 / 翌日 の暦日計算は共通 `DateStepper` が内部で行う (画面ごとの
   * `goToPrevDay` / `goToNextDay` は不要)。
   */
  onDateChange: (date: Date) => void
  onToday: () => void
  /**
   * - `'vertical'`: 一覧ページ (BalanceDemand / BalanceGeneration) の縦並び。
   *   design 8080 `balance/bg.html` / `balance/hatsu.html` の `.filter-date`
   *   (ラベル → 日付入力 → 日めくりナビ の 3 段)。
   * - `'horizontal'`: 詳細ページの横並び。design 8080 `balance/bg-region.html` /
   *   `balance/hatsu-detail.html` の `.filter-group.filter-date` (1 行)。
   */
  layout: 'vertical' | 'horizontal'
}

/**
 * 同時同量 4 画面 (需要 / 需要詳細 / 発電 / 発電詳細) 共通の「表示日」フィルタ。
 *
 * 日付入力と日めくりボタンは共通部品 `~/components/common/filters/DateStepper`
 * に委譲する。旧実装は同じ部品を手書きしており、DS `Input size="sm"` (高さ 36px /
 * 12px) + `TertiaryButton size="sm"` になっていて、design 8080 の
 * `.ecl-date-input` (高さ 40px / `--font-size-md` = 14px / `--border-mid` /
 * `--radius-base`) と `.btn-tertiary` (高さ 40px) から外れていた。
 *
 * ラベルは design 8080 の `.form-label` (= `--font-size-md` 14px / 600 /
 * `--text-high-emphasis`) = textStyle `label`。同カード内の他のフィルタ行ラベル
 * (基本表示 / 電源種別 等) と同じ役割・同じ書体で、`.filter-label` (md/700) では
 * ない (2026-08-12 に 8080 `balance/*.html` の実マークアップで確認)。
 * 旧実装は raw span に sm (12px) を直書きしており 2px 小さかった。
 */
export const DateNav = ({
  labelKeyPrefix,
  displayDate,
  onDateChange,
  onToday,
  layout,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // JST 0:00 を跨いだ瞬間に上限を更新する。上限は render 時計算なので、これが無いと
  // 画面を開いたまま日跨ぎした時に前日の上限が残り、新しい当日へ進めなくなる
  // (翌日ボタンが disabled のまま・日付入力の max も前日のまま)。同時同量 4 画面の
  // 5 秒ポーリングには頼れない — React Query は structural sharing により応答が
  // 同一だと再 render を起こさないため (codex review、 PR #6472)。
  useJstMidnightTick()

  const displayDateLabel = t(`${labelKeyPrefix}.displayDate`)

  // 同時同量は当日の実績・乖離を監視する画面であり、未来日には監視対象の実績値が
  // 存在しないため、未来日 (翌日以降) を選択不可にする (issue #6361、
  // doji-bg.md AC-BDB-004a / doji-hatsu.md AC-BDH-006a)。上限は useMemo にせず
  // render のたびに startOfTodayJst() を呼び直す (`pages/SpotPrice/index.tsx` の
  // `getMaxSelectableDate()` と同じ形) — useMemo で固定すると mount 時点の今日に
  // 凍結され、上の日跨ぎ tick も効かなくなる。
  //
  // maxDate を渡す呼び出し側として、無効理由の文言 (policy) もここで持つ
  // (`DateStepper` 側は中立フォールバックのみ持ち、feature 固有の理由を
  // 決め打ちしない — issue #6361 レビュー指摘)。
  const stepper = (
    <DateStepper
      dateAriaLabel={displayDateLabel}
      layout={layout === 'vertical' ? 'stackedCompact' : 'inline'}
      maxDate={startOfTodayJst()}
      maxDateDisabledReason={t('common:filter.nextDayDisabled')}
      onChange={onDateChange}
      onTodayClick={onToday}
      todayLabel={t(`${labelKeyPrefix}.today`)}
      value={displayDate}
    />
  )

  if (layout === 'vertical') {
    return (
      // 列幅は共通の日付欄の幅 (曜日付きの値が収まる幅)。 日付入力は stacked で
      // 全幅になるため、列幅としてここで持つ。 [design 差異] design `css/balance.css` の
      // `.filter-date .ecl-date-input { width:150px; max-width:150px }` は曜日の無い値の幅で、
      // 曜日を付けた (2026-10-20 デザイナー指示) 値が収まらない。
      <Box flexShrink={0} width={COMPACT_DATE_FIELD_WIDTH}>
        {/* ラベル下の余白 8px = design `.filter-date { gap:4px }` +
            `.filter-card .form-label { margin-bottom: var(--space-1) }` (4px)。 */}
        <Text textStyle="label" marginBottom={2}>
          {displayDateLabel}
        </Text>
        {stepper}
      </Box>
    )
  }

  // 横並びは design `.filter-group` (gap 8px) と同じく 1 つのまとまりにする
  // (親のフィルタ行の gap ではなく、ラベルと日付部品の間隔を design 値で持つ)。
  return (
    <Flex alignItems="center" flexWrap="wrap" gap={2}>
      {/* design も `<span class="form-label">` (同カード内の他の行ラベルと同じ) */}
      <Text as="span" textStyle="label" whiteSpace="nowrap">
        {displayDateLabel}
      </Text>
      {stepper}
    </Flex>
  )
}
